/**
 * This file contains interfaces and enums related to customers
 */
import * as v from 'valibot';

import { newSchema } from '../../utils/valibot/schema';

export interface CustomerContact {
  address?: CustomerAddress;
  email?: string;
  phone?: string;
  mobile?: string;
  bmwMiniId?: string;
}

export interface CustomerAddress {
  postalCode?: string;
  city?: string;
  houseNumber?: string;
  street?: string;
  province?: string;
  /** ISO country code consisting of two characters. E.g. DE, EN, US */
  country?: string;
  addressLine1?: string;
  addressLine2?: string;
  postBox?: string;
}

export interface DealerConsent {
  consentId: string;
  consentDate: string; // ISO date string format YYYY-MM-DD
  consent: DealerConsentOptions;
  legalEntities?: string[];
}

export interface DealerConsentUpdate {
  /** The date of the existing consent in ISO date string format YYYY-MM-DD, or undefined for new consents */
  consentDate?: string;

  consent: DealerConsentOptions;
  legalEntities?: string[];
}

export interface DealerConsentOptions {
  email: boolean;
  inCar: boolean;
  letter: boolean;
  phoneMessenger: boolean;
  customerProfile: boolean;
  dataProcessing: boolean;
}

export const DmsIdSchema = newSchema(
  'DmsIdSchema',
  v.object({
    /**
     * The ID which is used to identify the customer in the DMS system of the dealer given by the dealerId.
     * This ID is not unique and can be used by multiple dealers.
     * If undefined, the customer is not linked to a DMS system for the given dealerId.
     */
    dmsId: v.optional(v.string()),
    dealerId: v.string(),
  }),
);

export type DmsId = v.InferOutput<typeof DmsIdSchema>;

export enum MainAddress {
  BUSINESS = 'BUSINESS',
  PRIVATE = 'PRIVATE',
}

export enum VatGroup {
  STANDARD = 'STANDARD',
  REDUCED = 'REDUCED',
  NONE = 'NONE',
}

export enum CustomerTitle {
  DR = 'DR',
  PROF = 'PROF',
  PROF_DR = 'PROF_DR',
}

export enum CustomerSalutation {
  MR = 'MR',
  MS = 'MS',
  MX = 'MX',
  // GB/IE markets only
  MRS = 'MRS',
  MISS = 'MISS',
  DR = 'DR',
}

export enum NationalIdDocumentType {
  DRIVER_LICENCE = 'DRIVER_LICENCE',
  DRIVER_LICENSE_ID = 'DRIVER_LICENSE_ID',
  IDENTIFICATION_NUMBER = 'IDENTIFICATION_NUMBER',
  IDENTITY_CARD = 'IDENTITY_CARD',
  PASSPORT = 'PASSPORT',
  PASSPORT_ID = 'PASSPORT_ID',
  PERSON_ID = 'PERSON_ID',
  SOCIAL_SECURITY_NUMBER = 'SOCIAL_SECURITY_NUMBER',
  SOUTH_AFRICA_NATIONAL_ID = 'SOUTH_AFRICA_NATIONAL_ID',
}

export enum CustomerMainType {
  ORGANIZATION = 'ORGANIZATION',
  SELF_EMPLOYED = 'SELF_EMPLOYED',
  INDIVIDUAL = 'INDIVIDUAL',
  DEALER = 'DEALER', // This will never occur in production, as it is only set for non-customer accounts
}
